import { selectPlayerState } from "app/store/slices/player";
import { useAppSelector } from "app/store/store";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./player.module.scss";
import clsx from "clsx";
import { StationPlayer } from "./player.component";
import { Category, Stream } from "services-hooks/types";
import { useGetPillowRadioPlay } from "services-hooks/hooks";
import { notEmpty } from "app/ts";
import { useAudioAds } from "app/utils/audioAds";
import { useQueryClient } from "react-query";

const PlayerWrapper: React.FC = () => {
  const [playerOpen, setPlayerOpen] = useState(false);
  const { activeMedia } = useAppSelector(selectPlayerState);
  const activeCategory = activeMedia?.category;
  return activeCategory ? (
    <>
      <div className={clsx(styles.wrapper)}>
        <PlayerStreamsLoader
          category={activeCategory}
          playerOpen={playerOpen}
          setPlayerOpen={setPlayerOpen}
        />
      </div>
    </>
  ) : null;
};
interface PlayerStreamsLoaderProps {
  category: Category;
  playerOpen: boolean;
  setPlayerOpen: (open: boolean) => void;
}

const PlayerStreamsLoader: React.FC<PlayerStreamsLoaderProps> = ({
  category,
  playerOpen,
  setPlayerOpen,
}) => {
  const { radioQueue } = useAppSelector(selectPlayerState);
  const {
    data: streamsData,
    isLoading: streamsLoading,
    isError,
    refetch,
  } = useGetPillowRadioPlay(category.permalink!, {
    keepPreviousData: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const queryClient = useQueryClient();
  useEffect(() => {
    if (radioQueue.activeIndex !== null) {
      if (radioQueue.activeIndex === radioQueue.items.length - 1) {
        try {
          const queryNextInfo = useGetPillowRadioPlay.info(
            radioQueue.items[0].permalink!
          );
          const queryPrevInfo = useGetPillowRadioPlay.info(
            radioQueue.items[radioQueue.activeIndex - 1].permalink!
          );
          queryClient.prefetchQuery(queryPrevInfo.key, queryPrevInfo.fun);
          queryClient.prefetchQuery(queryNextInfo.key, queryNextInfo.fun);
        } catch (e) {}
      } else if (radioQueue.activeIndex === 0) {
        try {
          const queryNextInfo = useGetPillowRadioPlay.info(
            radioQueue.items[1].permalink!
          );
          const queryPrevInfo = useGetPillowRadioPlay.info(
            radioQueue.items[radioQueue.items.length - 1].permalink!
          );
          queryClient.prefetchQuery(queryPrevInfo.key, queryPrevInfo.fun);
          queryClient.prefetchQuery(queryNextInfo.key, queryNextInfo.fun);
        } catch (e) {}
      } else {
        try {
          const queryNextInfo = useGetPillowRadioPlay.info(
            radioQueue.items[radioQueue.activeIndex + 1].permalink!
          );
          const queryPrevInfo = useGetPillowRadioPlay.info(
            radioQueue.items[radioQueue.activeIndex - 1].permalink!
          );
          queryClient.prefetchQuery(queryPrevInfo.key, queryPrevInfo.fun);
          queryClient.prefetchQuery(queryNextInfo.key, queryNextInfo.fun);
        } catch (e) {}
      }
    }
  }, [radioQueue]);

  const streams: Stream[] | undefined = useMemo(() => {
    return (
      streamsData?.data?.body?.content?.streams
        ?.filter(notEmpty)
        .sort(
          (streamA, streamB) => (streamB.bitrate || 0) - (streamA.bitrate || 0)
        ) || []
    );
  }, [streamsData, category.permalink]);

  const {
    adStreams,
    adStreamsLoading,
    sendTargetspotTimeUpdateEvent,
    sendTrackingEvent,
    sendTargetspotStateUpdateEvent
  } = useAudioAds(category);

  return (
    <StationPlayer
      category={category}
      playerOpen={playerOpen}
      streamsLoadError={isError}
      refetchStreams={refetch}
      setPlayerOpen={setPlayerOpen}
      streams={streams}
      adStreams={adStreams}
      streamsLoading={streamsLoading || adStreamsLoading}
      sendTargetspotTimeUpdateEvent={sendTargetspotTimeUpdateEvent}
      sendTargetspotTrackingEvent={sendTrackingEvent}
      sendTargetspotStateUpdateEvent={sendTargetspotStateUpdateEvent}
    />
  );
};
export default PlayerWrapper;
